import {useState} from 'react';
import {useNavigate} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useSeparacion, useSeparar} from '../api/seguimiento';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

// Separar una exportacion en A y B (decision 0071): se marcan los contenedores
// que siguieron en otra reserva; se van a la parte B con sus Big Bags.
export function SepararExportacion({codigo, onCerrar}: {codigo: string; onCerrar: () => void}) {
  const consulta = useSeparacion(codigo, true);
  const separar = useSeparar(codigo);
  const navegar = useNavigate();
  const [elegidos, setElegidos] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const s = consulta.data;

  const alternar = (id: number) =>
    setElegidos(antes => {
      const nuevos = new Set(antes);
      if (nuevos.has(id)) nuevos.delete(id);
      else nuevos.add(id);
      return nuevos;
    });

  const confirmar = async () => {
    if (!s) return;
    setError(null);
    try {
      const r = await separar.mutateAsync([...elegidos]);
      void navegar(`/seguimiento/${encodeURIComponent(r.codigoA)}`, {replace: true});
      onCerrar();
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo separar.', 'Could not split.'));
    }
  };

  const deB = s ? s.contenedores.filter(c => elegidos.has(c.id)) : [];
  const deA = s ? s.contenedores.filter(c => !elegidos.has(c.id)) : [];
  const suma = (lista: typeof deB, campo: 'bigBags' | 'toneladasHumedas' | 'oroG') =>
    lista.reduce((t, c) => t + c[campo], 0);
  const oroTotal = s ? suma(s.contenedores, 'oroG') : 0;
  const porcentajeB = oroTotal > 0 ? Math.round((suma(deB, 'oroG') / oroTotal) * 1000) / 10 : null;

  return (
    <div className="space-y-3 rounded-lg border border-dorado bg-white p-4" data-prueba="separar-exportacion">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">{tr('✂ Separar en A y B', '✂ Split into A and B')}</h2>
        <button type="button" onClick={onCerrar} className="text-xs text-apagado hover:underline">
          {tr('Cerrar', 'Close')}
        </button>
      </div>
      {consulta.isPending && <p className="text-sm text-apagado">{tr('Consultando…', 'Loading…')}</p>}
      {consulta.isError && <p className="text-sm text-alerta">{consulta.error.message}</p>}
      {s && (
        <>
          <p className="max-w-3xl text-sm text-apagado">
            {tr(
              `Marca los contenedores que siguieron en otra reserva. ${s.codigo} pasa a llamarse ${s.codigoA} y se queda con los demás; se crea ${s.codigoB} con los marcados y sus Big Bags, con el mismo exportador, estado y fechas (después le cambias la reserva y las fechas nuevas).`,
              `Mark the containers that continued on another booking. ${s.codigo} is renamed ${s.codigoA} and keeps the rest; ${s.codigoB} is created with the marked ones and their Big Bags, with the same exporter, status and dates (then you change its booking and new dates).`,
            )}
          </p>
          {s.motivo ? (
            <p className="text-sm text-alerta">{s.motivo}</p>
          ) : (
            <>
              <table className="w-full max-w-2xl text-sm">
                <thead>
                  <tr className="text-left text-xs text-apagado">
                    <th className="py-1 pr-2 font-semibold">{tr('Va a', 'Goes to')}</th>
                    <th className="py-1 pr-2 font-semibold">{tr('Contenedor', 'Container')}</th>
                    <th className="py-1 pr-2 text-right font-semibold">Big Bags</th>
                    <th className="py-1 pr-2 text-right font-semibold">{tr('Ton. húmedas', 'Wet tons')}</th>
                    <th className="py-1 text-right font-semibold">{tr('Oro (g)', 'Gold (g)')}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.contenedores.map(c => (
                    <tr key={c.id} className="border-t border-linea/70">
                      <td className="py-1 pr-2">
                        <label className="inline-flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={elegidos.has(c.id)}
                            onChange={() => alternar(c.id)}
                            aria-label={tr(`${c.numero} va a ${s.codigoB}`, `${c.numero} goes to ${s.codigoB}`)}
                          />
                          <span className={elegidos.has(c.id) ? 'font-medium text-verde-oscuro' : 'text-apagado'}>
                            {elegidos.has(c.id) ? s.codigoB : s.codigoA}
                          </span>
                        </label>
                      </td>
                      <td className="py-1 pr-2">
                        {c.numero}
                        {c.provisional && (
                          <span className="ml-1 text-xs text-apagado">({tr('provisional', 'provisional')})</span>
                        )}
                      </td>
                      <td className="py-1 pr-2 text-right tabular-nums">{numero(c.bigBags)}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{numero(c.toneladasHumedas, 2)}</td>
                      <td className="py-1 text-right tabular-nums">{numero(c.oroG, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className="space-y-0.5 text-sm">
                <li>
                  <span className="font-medium">{s.codigoA}</span>:{' '}
                  {tr(
                    `${numero(deA.length)} contenedores, ${numero(suma(deA, 'bigBags') + s.bigBagsSinContenedor)} Big Bags`,
                    `${numero(deA.length)} containers, ${numero(suma(deA, 'bigBags') + s.bigBagsSinContenedor)} Big Bags`,
                  )}
                  {s.bigBagsSinContenedor > 0 &&
                    tr(
                      ` (${numero(s.bigBagsSinContenedor)} sin contenedor se quedan aquí)`,
                      ` (${numero(s.bigBagsSinContenedor)} without container stay here)`,
                    )}
                </li>
                <li>
                  <span className="font-medium">{s.codigoB}</span>:{' '}
                  {tr(
                    `${numero(deB.length)} contenedores, ${numero(suma(deB, 'bigBags'))} Big Bags`,
                    `${numero(deB.length)} containers, ${numero(suma(deB, 'bigBags'))} Big Bags`,
                  )}
                  {porcentajeB !== null && tr(` (${porcentajeB} % del oro)`, ` (${porcentajeB} % of the gold)`)}
                </li>
                {s.conFinanciacion && (
                  <li className="text-apagado">
                    {tr(
                      'La financiación se copia a la parte B con los montos repartidos por el oro; las dos quedan marcadas para revisar.',
                      'Financing is copied to part B with the amounts split by gold; both are flagged for review.',
                    )}
                  </li>
                )}
                {s.documentosDelExpediente > 0 && (
                  <li className="text-apagado">
                    {tr(
                      `Los ${numero(s.documentosDelExpediente)} documentos del expediente también quedan en ${s.codigoB}.`,
                      `The ${numero(s.documentosDelExpediente)} documents of the export file also stay in ${s.codigoB}.`,
                    )}
                  </li>
                )}
              </ul>
              {error && <p className="text-sm text-alerta">{error}</p>}
              <button
                type="button"
                disabled={!deB.length || !deA.length || separar.isPending}
                onClick={() => void confirmar()}
                className="rounded-md bg-verde px-4 py-2 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
              >
                {separar.isPending
                  ? tr('Separando…', 'Splitting…')
                  : tr(`Separar en ${s.codigoA} y ${s.codigoB}`, `Split into ${s.codigoA} and ${s.codigoB}`)}
              </button>
              {!deB.length && (
                <p className="text-xs text-apagado">
                  {tr('Marca al menos un contenedor para la parte B.', 'Mark at least one container for part B.')}
                </p>
              )}
              {deB.length > 0 && !deA.length && (
                <p className="text-xs text-apagado">
                  {tr('Deja al menos un contenedor en la parte A.', 'Leave at least one container in part A.')}
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
